import { api } from '@/lib/api/client';

export type StatisticPeriod = 'YTD' | 'MTD' | 'QTD' | 'HY';

// ── MD ──────────────────────────────────────────────────────────────────────

export interface MDPerformanceSummary {
  period: StatisticPeriod;
  period_start: string;
  plan: number;
  /** CASH — accepted payments received in the period (D-07). */
  revenue: {
    amount: number;
    percent: number;
    source: 'payments-accepted';
  };
  plan_achievement: { amount: number };
  /**
   * Cash MINUS PAYROLL — a different quantity from the P&L gross profit
   * (invoiced minus recorded expenses). `margin` is **null** when no cost has
   * been recorded, so a zero cost can never render as a 100% margin (D-08).
   */
  profit: {
    amount: number;
    margin: number | null;
    cost: number;
    costSource: 'salaries';
    costRecorded: boolean;
  };
  lost_revenue: { amount: number; percent: number };
  /** Both legs travel with the ratio so 86% can be checked on screen (D-09). */
  collection: {
    percent: number;
    collected: number;
    billed: number;
    billCount: number;
    basis: string;
  };
}

export interface RevenueGraphItem {
  month: string;
  revenue: number;
}

export interface MDPlSummaryGraphItem {
  month: string;
  cashIn: number;
  cashOut: number;
  balance: number;
}

export interface MDPlSummary {
  // P&L (accrual): total_revenue = billed, cost = expenses, gross_profit = Revenue − Costs.
  total_revenue: number;
  bill_count: number;
  /** ACCRUAL — issued bills. Distinct from the cash tile above (D-07). */
  revenue_source: 'bills-issued';
  cost: number;
  /** The Expenses module feeds this; a 0 means "no accepted expenses" (D-08). */
  cost_source: 'expenses-accepted';
  cost_recorded: boolean;
  expense_count: number;
  gross_profit: number;
  /** Null unless a cost was actually recorded — never 100% (D-08). */
  margin: number | null;
  // Cash flow (cash basis): cash_in = collected payments, cash_out = expenses.
  cash_in: number;
  cash_out: number;
  cash_balance: number;
  total_lost_time: number;
  lost_time_revenue: number;
  graph: MDPlSummaryGraphItem[];
}

export interface MDUtilizationLawyer {
  id: number;
  name: string | null;
  billableHours: number;
  capacityHours: number;
  utilization: number; // percent 0–100+
  /** Share of the firm's billable hours. Unlike `utilization`, these sum to 100%. */
  shareOfFirmBillable?: number;
}

export interface MDUtilizationPractice {
  practiceAreaId: number | null;
  name: string;
  billableHours: number;
  capacityHours: number;
  lawyerCount: number;
  utilization: number; // percent 0–100+
  shareOfFirmBillable: number;
  lawyers: MDUtilizationLawyer[];
}

export interface MDUtilization {
  firm: {
    billableHours: number;
    capacityHours: number;
    utilization: number;
    lawyerCount: number;
  };
  byPractice: MDUtilizationPractice[];
  /** D-05 — the by-lawyer breakdown the block was captioned for. */
  byLawyer: MDUtilizationLawyer[];
  /** D-05 — why practice shares don't sum to the firm figure. */
  denominators: {
    firm: string;
    practice: string;
    lawyer: string;
    capacityPerLawyer: number;
    workingDays: number;
    dailyHours: number;
    note: string;
  };
}

// D-04 — TS Lot 1 §7.1 Collection block: the overdue-invoice list.
export interface MDOverdueInvoice {
  id: number;
  clientId: number | null;
  clientName: string | null;
  contractId: number | null;
  currency: string;
  total: number;
  paid: number;
  outstanding: number;
  dueAt: string | null;
  issuedAt: string;
  daysOverdue: number;
}

export interface MDOverdueInvoices {
  invoices: MDOverdueInvoice[];
  count: number;
  shown: number;
  totalOutstanding: number;
  basis: string;
}

// Block 10 — Team Load table (per-lawyer Tasks / Cases / K1 / Utilization).
export interface MDTeamLoadLawyer {
  id: number;
  name: string | null;
  roleName: string | null;
  roleType: string | null;
  tasks: number; // open top-level tasks
  cases: number; // active matters this person holds a role on (per-user)
  /** K1 — weighted matter QA score (0–5); null when never QA-scored. */
  k1: number | null;
  /** K3 — manager rating (1–5); null when unreviewed. Never shown as K1. */
  k3: number | null;
  /** Numbers behind `utilization`. */
  billableHours?: number;
  capacityHours?: number;
  utilization: number; // percent 0–100+
}

export interface MDTeamLoad {
  lawyers: MDTeamLoadLawyer[];
}

// ── MD Team Activity (live "who's working right now" card) ───────────────────

/** Snapshot of one activity the member is (or was last) working on. */
export interface TeamActivitySnapshot {
  activityId: number;
  /** Free-text note the lawyer wrote on the activity (preferred label). */
  activityDescription: string | null;
  /** Activity-type name (e.g. "Drafting", "Research") — fallback label. */
  activityType: string | null;
  taskId: number | null;
  taskTitle: string | null;
  matterId: number | null;
  matterName: string | null;
}

/** A member with a running activity (open timer). */
export interface TeamActivityWorking extends TeamActivitySnapshot {
  /** ISO timestamp the running activity started — drives the live elapsed timer. */
  startedAt: string;
}

/** A member with no running activity — carries their most recent finished one. */
export interface TeamActivityLast extends TeamActivitySnapshot {
  /** ISO timestamp the last activity ended — drives the "x ago" label. */
  endedAt: string;
}

/**
 * Office presence, from today's latest `Attendee` row (arrive/leave check-in) —
 * independent of whether a time-tracking timer is running.
 */
export type AttendanceState = 'IN_OFFICE' | 'LEFT' | 'ABSENT';

export interface MemberAttendance {
  state: AttendanceState;
  /** ISO check-in time (null when ABSENT). */
  arrivedAt: string | null;
  /** ISO check-out time (null unless LEFT). */
  leavedAt: string | null;
}

export interface TeamActivityMember {
  userId: number;
  name: string;
  roleName: string | null;
  /** Lawyer role types only — managers are excluded server-side. */
  roleType: 'MD' | 'PARTNER' | 'LEAD' | 'STAFF';
  attendance: MemberAttendance;
  current: TeamActivityWorking | null;
  last: TeamActivityLast | null;
}

export interface TeamActivityResponse {
  members: TeamActivityMember[];
}

export interface MDTeamEfficiencyLawyer {
  id: number;
  name: string | null;
  /** Overall KPI score (%) for the period, or null when there's no data. */
  kpi: number | null;
}

export interface MDTeamEfficiency {
  /** Firm average KPI (%) — mean of all scored lawyers, computed server-side. */
  firm_kpi: number;
  by_lawyer: MDTeamEfficiencyLawyer[];
}

// Quality block (§7.1 "Average K1 firm-wide, Top / Bottom performers").
// K1 = weighted reviewer QA score (MatterQaScore, 0–5) — the §6.3 quality index.
// K3 = manager rating (Review.rating, 1–5), reported alongside but never as K1.
// The two were conflated until 2026-08-03: the block showed the client/manager
// review average and labelled it K1.
export interface MDWorkQualityLawyer {
  id: number;
  name: string | null;
  /** K1 — weighted QA score, null when never QA-scored in the period. */
  k1: number | null;
  /** How many QA scores `k1` averages. */
  k1_scores: number;
  /** K3 — manager rating, null when unreviewed. */
  k3: number | null;
  k3_reviews: number;
}

export interface MDWorkQuality {
  /** Firm-wide Average K1 (§7.1). Null when nothing was QA-scored. */
  avg_k1: number | null;
  k1_scale: number;
  k1_rated_lawyers: number;
  avg_k3: number | null;
  k3_scale: number;
  k3_rated_lawyers: number;
  by_lawyer: MDWorkQualityLawyer[];
}

// Write-off, Adjustments & Escalations (2026-06-06, real data):
// write_off = % of worked seconds the client doesn't pay for (DONE/BILLED tasks),
// adjustments = % of worked seconds inside adjustment tasks, escalations = % of
// tasks created in the period that escalated at least once.
export interface MDWriteOffTrendPoint {
  /** Day of month for MTD, short month name otherwise. */
  label: string;
  write_off: number;
  adjustments: number;
  escalations: number;
}

export interface MDWriteOff {
  period: StatisticPeriod;
  period_start: string;
  write_off: {
    percent: number;
    worked_seconds: number;
    billable_seconds: number;
    lost_seconds: number;
  };
  adjustments: {
    percent: number;
    count: number;
    seconds: number;
    /** Denominator of `percent`: all worked seconds incl. adjustments. */
    base_seconds: number;
  };
  escalations: { percent: number; escalated: number; total_tasks: number };
  trend: MDWriteOffTrendPoint[];
}

// TZ Lot 1 §7.1 / §8.2 — "high share of expensive roles" alert. Current state
// of every active Fixed Fee case over the threshold (the daily cron only sends
// notifications and stops reporting a case once it has alerted).
export interface MDExpensiveRoleMatter {
  id: number;
  name: string | null;
  billableHours: number;
  expensiveHours: number;
  /** Expensive share of billable hours, %. */
  share: number;
}

export interface MDExpensiveRoles {
  thresholds: { ratePerHour: number; share: number; minBillableHours: number };
  count: number;
  matters: MDExpensiveRoleMatter[];
  scope: string;
}

export interface MDNps {
  nps: number | null;
  total: number;
  promoters: number;
  passives: number;
  detractors: number;
}

export interface MDClientDebt {
  last_30_day: number;
  from_30_to_60: number;
  from_60_to_90: number;
  more_than_90: number;
}

// ── Accountant ───────────────────────────────────────────────────────────────

export interface AccountantReportsGraphItem {
  practiceArea: string;
  amount: number;
}

export interface AccountantReports {
  collection_rate: number;
  write_off: number;
  write_off_percent: number;
  graph: AccountantReportsGraphItem[];
}

// ── Lawyer ───────────────────────────────────────────────────────────────────

export interface LawyerKpi {
  active_matters: number;
  /** Hours of tasks APPROVED (DONE/BILLED) this calendar month. */
  month_hours: number;
  utilization: number;
  /** Avg of received manager reviews (K3, 1–5), rolling 12 months; null when unrated. */
  avg_rating: number | null;
  /** The scale `avg_rating` is on — 5 since 2026-08-03 (was 10). */
  rating_scale?: number;
  review_count: number;
}

export interface LawyerScheduleItem {
  id: number;
  /** Task status — returned by the `deadlines` type (Draft / Lead-rejected rows). */
  newStatus?: string;
  description: string;
  matter_name: string | null;
  matter_id: number | null;
  client_name: string;
  client_id: number | null;
  dueAt: string;
  left_days: number;
}

// Upcoming-meetings card (2026-06-06): calendar Events the user attends or
// organizes — future/ongoing only, soonest first, paginated.
export interface LawyerMeetingItem {
  id: number;
  description: string | null;
  startsAt: string;
  endsAt: string;
  location: string | null;
  target: 'FIRM_EVENT' | 'NEW_CLIENT' | 'CASE_MEETING';
  matterId: number | null;
  matterName: string | null;
  clientId: number | null;
  clientName: string | null;
  attendeeCount: number;
  isOrganizer: boolean;
}

export interface LawyerMeetingsResponse {
  total: number;
  data: LawyerMeetingItem[];
}

// "My Cases" card (2026-06-06): active matters where the user is Lead or
// Contributor (lawyer endpoint) or Partner (partner endpoint), with role,
// open-task count and nearest task deadline. For PARTNER rows the task
// count/deadline are matter-wide (partners oversee, they don't execute).
export interface LawyerMatterItem {
  id: number;
  name: string;
  clientId: number | null;
  clientName: string | null;
  isCourtCase: boolean;
  phase: string;
  myRole: 'LEAD' | 'CONTRIBUTOR' | 'PARTNER';
  myOpenTasks: number;
  nextDueAt: string | null;
}

export interface LawyerMattersResponse {
  total: number;
  data: LawyerMatterItem[];
}

// ── Partner ──────────────────────────────────────────────────────────────────

export interface PartnerTeamMember {
  lawyerId: number;
  lawyer_full_name: string;
  lawyerRole: string;
  /** Billable share of the lawyer's own tracked time on the partner's matters. */
  utilization: number;
  hours: number;
  pending_hours: number;
  avg_rating?: number | null;
}

export interface PartnerTeamResponse {
  total: number;
  data: PartnerTeamMember[];
}

// ── Case 360° (Stage 12) — Dashboards & Alerts ──────────────────────────────

export interface Case360Delegation {
  id: number;
  matterId: number;
  matterName: string | null;
  fromUserId: number;
  fromUserName: string | null;
  toUserId?: number;
  toUserName?: string | null;
  /** Who requested the lead change (Lead/Partner/MD) — null on old rows. */
  initiatedById?: number | null;
  initiatedByName?: string | null;
  forced?: boolean;
  createdAt: string;
}

export interface Case360ChangeRequest {
  id: number;
  type: 'SCOPE' | 'BUDGET' | 'TIMELINE' | 'TEAM';
  reason: string;
  createdAt: string;
  matter?: { id: number; name: string | null } | null;
  author?: { id: number; name: string | null } | null;
}

export interface Case360My {
  delegations: Case360Delegation[];
  changeRequests: Case360ChangeRequest[];
}

export interface Case360ComplianceItem {
  id: number;
  name: string | null;
  phase: string;
  createdAt: string;
  client?: { name: string | null } | null;
}

export interface Case360EmergencyItem {
  id: number;
  name: string | null;
  emergencyExpiresAt: string | null;
  emergencyExtensions: number;
}

// Stage 10 — a break-glass request awaiting a Partner/MD decision (PENDING).
// The auditor is the requester; access to one SECRET document inside a matter.
export interface Case360BreakGlassItem {
  id: number;
  reason: string;
  createdAt: string;
  document: { id: number; name: string; matterId: number } | null;
  auditor: { id: number; name: string | null } | null;
}

// Stage 13 / Lot 1 — a bill awaiting approval (Bill.state = PENDING).
export interface Case360BillingItem {
  id: number;
  total: number;
  currency: string;
  description: string | null;
  contract: { id: number; client: { name: string | null } | null } | null;
}

export interface Case360RateAlert {
  rate: number;
  level: 'ok' | 'yellow' | 'red';
}

export interface Case360BypassAlert extends Case360RateAlert {
  bypassed: number;
  total: number;
  mdOverride: boolean;
  blocked: boolean;
}

export interface Case360EmergencyAlert extends Case360RateAlert {
  opened: number;
  total: number;
  windowDays: number;
}

// Stage 13 — matters whose billed total crossed 75% (yellow) / 80% (red) of
// Matter.budget. Live computation: an approved BUDGET CR re-baselines it.
export interface Case360BudgetAlertMatter {
  id: number;
  name: string | null;
  budget: number;
  billed: number;
  rate: number;
  level: 'yellow' | 'red';
}

export interface Case360BudgetAlert {
  yellow: number;
  red: number;
  matters: Case360BudgetAlertMatter[];
}

// Firm-wide efficiency alerts (§7.1). Escalation rate red above 5%;
// Realization (billable ÷ worked time) red below 0.8.
export interface Case360EscalationAlert extends Case360RateAlert {
  escalated: number;
  total: number;
  windowDays: number;
}

export interface Case360RealizationAlert {
  value: number;
  billable: number;
  worked: number;
  /** Rolling window the value covers — not the page's period selector. */
  windowDays: number;
  level: 'ok' | 'red';
}

export interface Case360TeamLoadItem {
  id: number;
  name: string | null;
  roleName: string | null;
  roleType: string | null;
  openTasks: number;
  activeMatters: number;
}

// Counts only for the six Pending-Approval tiles (no lists).
export interface Case360PendingCounts {
  compliance: number;
  emergency: number;
  breakGlass: number;
  delegation: number;
  billing: number;
  changeRequests: number;
}

export interface Case360Governance {
  approvals: {
    compliance: { total: number; data: Case360ComplianceItem[] };
    emergencies: Case360EmergencyItem[];
    delegations: Case360Delegation[];
    changeRequests: Case360ChangeRequest[];
    breakGlass: Case360BreakGlassItem[];
    billing: Case360BillingItem[];
  };
  teamLoad: Case360TeamLoadItem[];
  alerts: {
    bypass: Case360BypassAlert;
    emergency: Case360EmergencyAlert;
    budget: Case360BudgetAlert;
    escalation: Case360EscalationAlert;
    realization: Case360RealizationAlert;
  };
}

// ── Yearly plan ──────────────────────────────────────────────────────────────

export interface YearlyPlan {
  id: number;
  amount: number;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

interface YearlyPlanListResponse {
  meta: { offset: number; limit: number; total: number };
  results: YearlyPlan[];
}

// ── API ──────────────────────────────────────────────────────────────────────

export const dashboardApi = {
  // MD
  getMDStatistics: (period: StatisticPeriod): Promise<MDPerformanceSummary> =>
    api.get<MDPerformanceSummary>('/statistic/md/kpi', { params: { period } }),

  getMDNps: (period: StatisticPeriod): Promise<MDNps> =>
    api.get<MDNps>('/statistic/md/nps', { params: { period } }),

  getMDRevenueGraph: (period: StatisticPeriod): Promise<RevenueGraphItem[]> =>
    api.get<RevenueGraphItem[]>('/statistic/md/revenue-graph', { params: { period } }),

  getMDPlSummary: (period: StatisticPeriod): Promise<MDPlSummary> =>
    api.get<MDPlSummary>('/statistic/md/pl-summary', { params: { period } }),

  getMDTeamEfficiency: (range: { from: string; to: string }): Promise<MDTeamEfficiency> =>
    // `period` is ignored by the backend when from+to are set, but the shared
    // StatisticQueryDto still needs a valid value, so we send a constant.
    api.get<MDTeamEfficiency>('/statistic/md/team-efficiency', {
      params: { period: 'YTD', from: range.from, to: range.to },
    }),

  getMDWorkQuality: (period: StatisticPeriod): Promise<MDWorkQuality> =>
    api.get<MDWorkQuality>('/statistic/md/work-quality', { params: { period } }),

  getMDClientDebt: (period: StatisticPeriod): Promise<MDClientDebt> =>
    api.get<MDClientDebt>('/statistic/md/client-debt', { params: { period } }),

  getMDWriteOff: (period: StatisticPeriod): Promise<MDWriteOff> =>
    api.get<MDWriteOff>('/statistic/md/write-off', { params: { period } }),

  getMDUtilization: (period: StatisticPeriod): Promise<MDUtilization> =>
    api.get<MDUtilization>('/statistic/md/utilization', { params: { period } }),

  // Current state, not the cron's alert history — the service ignores `period`.
  // ⚠ It must still be SENT: every `/statistic/md/:type` route shares
  // `StatisticQueryDto`, where `period` is required, so omitting it 400s before
  // the service is reached. Same reason `team-activity` sends a constant.
  getMDExpensiveRoles: (): Promise<MDExpensiveRoles> =>
    api.get<MDExpensiveRoles>('/statistic/md/expensive-roles', {
      params: { period: 'YTD' },
    }),

  getMDTeamLoad: (period: StatisticPeriod): Promise<MDTeamLoad> =>
    api.get<MDTeamLoad>('/statistic/md/team-load', { params: { period } }),

  // D-04 — overdue invoices. Read-only; `period` is a constant only because the
  // shared StatisticQueryDto requires it (the list is always "as of now").
  getMDOverdueInvoices: (limit = 20): Promise<MDOverdueInvoices> =>
    api.get<MDOverdueInvoices>('/statistic/md/overdue-invoices', {
      params: { period: 'YTD', limit },
    }),

  // `period` is sent as a constant only to satisfy the shared StatisticQueryDto
  // (required there); the team-activity service ignores it.
  getMDTeamActivity: (): Promise<TeamActivityResponse> =>
    api.get<TeamActivityResponse>('/statistic/md/team-activity', { params: { period: 'MTD' } }),

  // Accountant
  getAccountantReports: (period: StatisticPeriod): Promise<AccountantReports> =>
    api.get<AccountantReports>('/statistic/accountant/reports', { params: { period } }),

  getAccountantClientDebt: (period: StatisticPeriod): Promise<MDClientDebt> =>
    api.get<MDClientDebt>('/statistic/accountant/client-debt', { params: { period } }),

  // Lawyer
  getLawyerKpi: (): Promise<LawyerKpi> =>
    api.get<LawyerKpi>('/statistic/lawyer/kpi'),

  // Upcoming events the user attends/organizes; limit: 0 = no cap.
  getLawyerMeetings: (params?: { limit?: number; offset?: number }): Promise<LawyerMeetingsResponse> =>
    api.get<LawyerMeetingsResponse>('/statistic/lawyer/meeting', { params }),

  // limit: 0 = no cap (the card's "Show all"); role filters server-side.
  getLawyerMyMatters: (params?: {
    limit?: number;
    offset?: number;
    role?: 'LEAD' | 'CONTRIBUTOR';
  }): Promise<LawyerMattersResponse> =>
    api.get<LawyerMattersResponse>('/statistic/lawyer/my-matters', { params }),

  getLawyerDeadlines: (): Promise<LawyerScheduleItem[]> =>
    api.get<LawyerScheduleItem[]>('/statistic/lawyer/deadlines'),

  // Partner
  getPartnerMyTeam: (params?: { matterId?: number; limit?: number; offset?: number }): Promise<PartnerTeamResponse> =>
    api.get<PartnerTeamResponse>('/statistic/partner/my-team', { params }),

  // Same item shape as the lawyer my-matters (myRole = PARTNER, matter-wide
  // task counts); limit: 0 = no cap.
  getPartnerMyMatters: (params?: { limit?: number; offset?: number }): Promise<LawyerMattersResponse> =>
    api.get<LawyerMattersResponse>('/statistic/partner/my-matters', { params }),

  // Case 360° (Stage 12)
  getCase360My: (): Promise<Case360My> =>
    api.get<Case360My>('/statistic/case360/my'),

  getCase360Governance: (): Promise<Case360Governance> =>
    api.get<Case360Governance>('/statistic/case360/governance'),

  getCase360PendingApprovals: (): Promise<Case360PendingCounts> =>
    api.get<Case360PendingCounts>('/statistic/case360/pending-approvals'),

  // Yearly plan
  getYearlyPlan: async (): Promise<YearlyPlan | null> => {
    const res = await api.get<YearlyPlanListResponse>('/yearly-plan');
    return res.results[0] ?? null;
  },

  saveYearlyPlan: (amount: number, id?: number): Promise<YearlyPlan> =>
    id
      ? api.patch<YearlyPlan>(`/yearly-plan/${id}`, { amount })
      : api.post<YearlyPlan>('/yearly-plan', { amount }),
};

export const dashboardQueryKeys = {
  all: ['dashboard'] as const,
  // MD
  mdStatistics: (period: StatisticPeriod) => [...dashboardQueryKeys.all, 'md', 'statistics', period] as const,
  mdNps: (period: StatisticPeriod) => [...dashboardQueryKeys.all, 'md', 'nps', period] as const,
  mdRevenueGraph: (period: StatisticPeriod) => [...dashboardQueryKeys.all, 'md', 'revenueGraph', period] as const,
  mdPlSummary: (period: StatisticPeriod) => [...dashboardQueryKeys.all, 'md', 'plSummary', period] as const,
  mdTeamEfficiency: (from?: string, to?: string) => [...dashboardQueryKeys.all, 'md', 'teamEfficiency', from, to] as const,
  mdWorkQuality: (period: StatisticPeriod) => [...dashboardQueryKeys.all, 'md', 'workQuality', period] as const,
  mdClientDebt: (period: StatisticPeriod) => [...dashboardQueryKeys.all, 'md', 'clientDebt', period] as const,
  mdWriteOff: (period: StatisticPeriod) => [...dashboardQueryKeys.all, 'md', 'writeOff', period] as const,
  mdUtilization: (period: StatisticPeriod) => [...dashboardQueryKeys.all, 'md', 'utilization', period] as const,
  mdExpensiveRoles: () => [...dashboardQueryKeys.all, 'md', 'expensiveRoles'] as const,
  mdTeamLoad: (period: StatisticPeriod) => [...dashboardQueryKeys.all, 'md', 'teamLoad', period] as const,
  mdTeamActivity: () => [...dashboardQueryKeys.all, 'md', 'teamActivity'] as const,
  mdOverdueInvoices: () => [...dashboardQueryKeys.all, 'md', 'overdueInvoices'] as const,
  // Accountant
  accountantReports: (period: StatisticPeriod) => [...dashboardQueryKeys.all, 'accountant', 'reports', period] as const,
  accountantClientDebt: (period: StatisticPeriod) => [...dashboardQueryKeys.all, 'accountant', 'clientDebt', period] as const,
  // Lawyer
  lawyerKpi: () => [...dashboardQueryKeys.all, 'lawyer', 'kpi'] as const,
  lawyerMeetings: (limit?: number) => [...dashboardQueryKeys.all, 'lawyer', 'meetings', limit ?? 10] as const,
  lawyerMyMatters: (role?: string, limit?: number) => [...dashboardQueryKeys.all, 'lawyer', 'myMatters', role ?? 'ALL', limit ?? 10] as const,
  lawyerDeadlines: () => [...dashboardQueryKeys.all, 'lawyer', 'deadlines'] as const,
  // Partner
  partnerDocumentsToReview: () => [...dashboardQueryKeys.all, 'partner', 'documentsToReview'] as const,
  partnerMyTeam: () => [...dashboardQueryKeys.all, 'partner', 'myTeam'] as const,
  partnerApprovalQueue: () => [...dashboardQueryKeys.all, 'partner', 'approvalQueue'] as const,
  partnerMyMatters: (limit?: number) => [...dashboardQueryKeys.all, 'partner', 'myMatters', limit ?? 10] as const,
  // Case 360° (Stage 12)
  case360My: () => [...dashboardQueryKeys.all, 'case360', 'my'] as const,
  case360Governance: () => [...dashboardQueryKeys.all, 'case360', 'governance'] as const,
  case360PendingApprovals: () => [...dashboardQueryKeys.all, 'case360', 'pendingApprovals'] as const,
  // Yearly plan
  yearlyPlan: () => [...dashboardQueryKeys.all, 'yearlyPlan'] as const,
};
